import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { CategorySelectorComponent } from '../../shared/design-system/category-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { MarketplaceStoreSelectorComponent } from '../../shared/design-system/marketplace-store-selector.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';
import {
  CatalogService,
  CategoryResponse,
  OfferingResponse,
  StorefrontCategoryPlacement,
  StorefrontNavigationItem
} from './catalog.service';
import {
  STOREFRONT_NAV_LOCATION_KEYS,
  STOREFRONT_NAV_TARGET_TYPES
} from './marketplace-storefront-selectors.model';
import {
  MARKETPLACE_LIST_TOOLBAR_STYLES,
  MarketplaceLiveSubscription,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeMarketplaceOperationsLiveUpdates,
  toggleSortField,
  unsubscribeMarketplaceOperationsLiveUpdates
} from './marketplace-list-page.utils';

type PlacementSortField = 'categoryId' | 'displayOrder' | 'isVisible' | 'isFeatured' | 'showInNavigation';
type NavSortField = 'locationKey' | 'displayOrder' | 'targetType' | 'isVisible';

@Component({
  standalone: true,
  selector: 'ain-storefront-category-navigation-manager',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    StructuredJsonFormComponent,
    MarketplaceStoreSelectorComponent,
    CategorySelectorComponent,
    OfferingSelectorComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    CheckboxModule,
    SelectModule
  ],
  template: `
    <ain-page
      [title]="t('catalog.storefront.categoryNavigation.title')"
      [description]="t('catalog.storefront.categoryNavigation.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.marketplace.help.list.title'"
        [descriptionKey]="'catalog.marketplace.help.list.description'"
        [bulletKeys]="[
          'catalog.marketplace.help.list.bullets.selection',
          'catalog.marketplace.help.list.bullets.filters',
          'catalog.marketplace.help.list.bullets.live'
        ]" />

      <div class="toolbar store-toolbar">
        <ain-marketplace-store-selector
          class="store-selector"
          [(ngModel)]="selectedStoreId"
          (ngModelChange)="onStoreChange()" />
        <span class="live-status" [class]="liveUpdateService.connectionState()">
          {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
        </span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="card panel">
          <h3>{{ t('catalog.storefront.categoryNavigation.placements') }}</h3>
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.storefront.categoryNavigation.addPlacement')" (click)="openPlacementDialog()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.bulk')" (click)="applyPlacementBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.export')" (click)="exportPlacements()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('catalog.marketplace.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'catalog.marketplace.help.filters.title'"
                [descriptionKey]="'catalog.marketplace.help.filters.description'"
                [bulletKeys]="['catalog.marketplace.help.filters.bullets.search']" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('catalog.marketplace.filters.search') }}</span>
                <input pInputText [value]="searchPlacements()" (input)="onPlacementSearch($event)" />
              </label>
            </div>
          </details>

          @if (filteredPlacements().length === 0) {
            <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
          } @else {
            <p-table
              [value]="filteredPlacements()"
              dataKey="id"
              [paginator]="true"
              [rows]="15"
              [selection]="selectedPlacements()"
              (selectionChange)="onPlacementSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th><button type="button" class="sort-btn" [class.active]="placementSortField() === 'categoryId'" (click)="sortPlacementsBy('categoryId')">{{ t('catalog.storefront.categoryNavigation.fields.category') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="placementSortField() === 'isVisible'" (click)="sortPlacementsBy('isVisible')">{{ t('catalog.storefront.categoryNavigation.fields.visible') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="placementSortField() === 'isFeatured'" (click)="sortPlacementsBy('isFeatured')">{{ t('catalog.storefront.categoryNavigation.fields.featured') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="placementSortField() === 'showInNavigation'" (click)="sortPlacementsBy('showInNavigation')">{{ t('catalog.storefront.categoryNavigation.fields.showInNav') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="placementSortField() === 'displayOrder'" (click)="sortPlacementsBy('displayOrder')">{{ t('catalog.storefront.publishing.order') }}</button></th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td>{{ categoryLabel(row.categoryId) }}</td>
                  <td>{{ row.isVisible ? t('common.yes') : t('common.no') }}</td>
                  <td>{{ row.isFeatured ? t('common.yes') : t('common.no') }}</td>
                  <td>{{ row.showInNavigation ? t('common.yes') : t('common.no') }}</td>
                  <td>{{ row.displayOrder }}</td>
                  <td><button pButton type="button" size="small" severity="secondary" [label]="t('common.edit')" (click)="openEditPlacementDialog(row)"></button></td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        <section class="card panel">
          <h3>{{ t('catalog.storefront.categoryNavigation.navigation') }}</h3>
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.storefront.categoryNavigation.addNav')" (click)="openNavDialog()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.bulk')" (click)="applyNavBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.export')" (click)="exportNavigation()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('catalog.marketplace.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'catalog.marketplace.help.filters.title'"
                [descriptionKey]="'catalog.marketplace.help.filters.description'"
                [bulletKeys]="['catalog.marketplace.help.filters.bullets.search']" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('catalog.marketplace.filters.search') }}</span>
                <input pInputText [value]="searchNav()" (input)="onNavSearch($event)" />
              </label>
            </div>
          </details>

          @if (filteredNavigation().length === 0) {
            <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
          } @else {
            <p-table
              [value]="filteredNavigation()"
              dataKey="id"
              [paginator]="true"
              [rows]="15"
              [selection]="selectedNav()"
              (selectionChange)="onNavSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th><button type="button" class="sort-btn" [class.active]="navSortField() === 'locationKey'" (click)="sortNavBy('locationKey')">{{ t('catalog.storefront.categoryNavigation.fields.location') }}</button></th>
                  <th>{{ t('catalog.storefront.categoryNavigation.fields.label') }}</th>
                  <th><button type="button" class="sort-btn" [class.active]="navSortField() === 'targetType'" (click)="sortNavBy('targetType')">{{ t('catalog.storefront.categoryNavigation.fields.target') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="navSortField() === 'isVisible'" (click)="sortNavBy('isVisible')">{{ t('catalog.storefront.categoryNavigation.fields.visible') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="navSortField() === 'displayOrder'" (click)="sortNavBy('displayOrder')">{{ t('catalog.storefront.publishing.order') }}</button></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td>{{ navLocationLabel(row.locationKey) }}</td>
                  <td>{{ navLabel(row.labelJson) }}</td>
                  <td>{{ navTargetLabel(row.targetType, row.targetValue) }}</td>
                  <td>{{ row.isVisible ? t('common.yes') : t('common.no') }}</td>
                  <td>{{ row.displayOrder }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        @if (info()) { <p class="info">{{ info() }}</p> }
      }

      <p-dialog [header]="editingPlacement ? t('catalog.storefront.categoryNavigation.editPlacement') : t('catalog.storefront.categoryNavigation.addPlacement')" [(visible)]="placementDialogVisible" [modal]="true" [style]="{ width: '560px' }">
        <div class="form">
          @if (editingPlacement) {
            <p class="section-hint"><strong>{{ categoryLabel(selectedCategoryId ?? '') }}</strong></p>
          } @else {
            <p class="section-hint">{{ t('catalog.storefront.categoryNavigation.help.categoriesSource') }}</p>
            <label>
              <span>{{ t('catalog.marketplace.filters.store') }}</span>
              <ain-marketplace-store-selector [(ngModel)]="placementDialogStoreId" class="full" />
            </label>
            <label>
              <span>{{ t('catalog.storefront.categoryNavigation.fields.category') }}</span>
              <ain-category-selector [(ngModel)]="selectedCategoryId" [reloadToken]="categoryReloadToken" class="full" />
            </label>
            <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.storefront.categoryNavigation.createCategory')" (click)="openCreateCategoryDialog()"></button>
          }
          <label>
            <span>{{ t('catalog.storefront.publishing.order') }}</span>
            <input pInputText type="number" [(ngModel)]="placementOrder" class="full" />
          </label>
          <div class="row"><p-checkbox [(ngModel)]="placementVisible" [binary]="true" inputId="plVis" /><label for="plVis">{{ t('catalog.storefront.categoryNavigation.fields.visible') }}</label></div>
          <div class="row"><p-checkbox [(ngModel)]="placementFeatured" [binary]="true" inputId="plF" /><label for="plF">{{ t('catalog.storefront.categoryNavigation.fields.featured') }}</label></div>
          <div class="row"><p-checkbox [(ngModel)]="placementShowInNav" [binary]="true" inputId="plNav" /><label for="plNav">{{ t('catalog.storefront.categoryNavigation.fields.showInNav') }}</label></div>
          @if (placementDialogError()) { <p class="error">{{ placementDialogError() }}</p> }
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closePlacementDialog()"></button>
          <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="acting()" (click)="savePlacement()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.storefront.categoryNavigation.createCategoryTitle')" [(visible)]="createCategoryDialogVisible" [modal]="true" [style]="{ width: '440px' }">
        <div class="form">
          <label>
            <span>{{ t('catalog.storefront.categoryNavigation.fields.categoryName') }}</span>
            <input pInputText [(ngModel)]="newCategoryName" class="full" />
          </label>
          <label>
            <span>{{ t('catalog.storefront.categoryNavigation.fields.categorySlug') }}</span>
            <input pInputText [(ngModel)]="newCategorySlug" class="full" />
          </label>
          @if (createCategoryError()) { <p class="error">{{ createCategoryError() }}</p> }
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createCategoryDialogVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.action.create')" [disabled]="acting()" (click)="submitCreateCategory()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.storefront.categoryNavigation.addNav')" [(visible)]="navDialogVisible" [modal]="true" [style]="{ width: '560px' }">
        <div class="form">
          <label>
            <span>{{ t('catalog.marketplace.filters.store') }}</span>
            <ain-marketplace-store-selector [(ngModel)]="navDialogStoreId" class="full" />
          </label>
          <label>{{ t('catalog.storefront.categoryNavigation.fields.location') }}</label>
          <p-select
            [options]="navLocationOptions"
            optionValue="value"
            [(ngModel)]="navLocationKey"
            appendTo="body"
            class="full">
            <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('catalog.selectors.navLocation.placeholder') }}</ng-template>
            <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
          </p-select>
          <ain-structured-json-form [fields]="navLabelFields" [(payloadJson)]="navLabelJson" />
          <label>{{ t('catalog.storefront.categoryNavigation.fields.target') }}</label>
          <p-select
            [options]="navTargetTypeOptions"
            optionValue="value"
            [(ngModel)]="navTargetType"
            (ngModelChange)="onNavTargetTypeChange()"
            appendTo="body"
            class="full">
            <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
            <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
          </p-select>
          @if (navTargetType === 'Category') {
            <label>{{ t('catalog.storefront.categoryNavigation.fields.category') }}</label>
            <ain-category-selector [(ngModel)]="navTargetValue" class="full" />
          } @else if (navTargetType === 'Offering') {
            <label>{{ t('catalog.storefront.publishing.offering') }}</label>
            <ain-offering-selector [(ngModel)]="navTargetValue" class="full" />
          } @else {
            <label>{{ t('catalog.selectors.navTarget.route') }}</label>
            <input pInputText [(ngModel)]="navTargetValue" class="full" />
          }
          <label>{{ t('catalog.storefront.publishing.order') }}</label>
          <input pInputText type="number" [(ngModel)]="navDisplayOrder" class="full" />
          <div class="row"><p-checkbox [(ngModel)]="navIsVisible" [binary]="true" inputId="navVisible" /><label for="navVisible">{{ t('catalog.storefront.categoryNavigation.fields.visible') }}</label></div>
          @if (navDialogError()) { <p class="error">{{ navDialogError() }}</p> }
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeNavDialog()"></button>
          <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="acting()" (click)="saveNav()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .store-toolbar { margin-bottom: 8px; }
    .store-selector { width: 320px; }
    .row { display: flex; gap: 8px; align-items: center; }
    h3 { margin: 0; }
    .form label > span { color: #475569; font-size: 12px; font-weight: 600; }
    .section-hint { margin: 0; color: #64748b; font-size: 13px; }
    .error { margin: 0; color: #b91c1c; }
    ${MARKETPLACE_LIST_TOOLBAR_STYLES}
  `]
})
export class StorefrontCategoryNavigationManagerComponent implements OnInit, OnDestroy {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private marketplaceLive: MarketplaceLiveSubscription = { topic: null, eventsSubscription: null };

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly info = signal('');
  readonly placementDialogError = signal('');
  readonly navDialogError = signal('');
  readonly createCategoryError = signal('');
  readonly categories = signal<CategoryResponse[]>([]);
  readonly offerings = signal<OfferingResponse[]>([]);
  readonly placements = signal<StorefrontCategoryPlacement[]>([]);
  readonly navigationItems = signal<StorefrontNavigationItem[]>([]);
  readonly selectedPlacements = signal<StorefrontCategoryPlacement[]>([]);
  readonly selectedNav = signal<StorefrontNavigationItem[]>([]);
  readonly searchPlacements = signal('');
  readonly searchNav = signal('');
  readonly placementSortField = signal<PlacementSortField>('displayOrder');
  readonly placementSortOrder = signal<1 | -1>(1);
  readonly navSortField = signal<NavSortField>('displayOrder');
  readonly navSortOrder = signal<1 | -1>(1);

  liveStatusKey = liveStatusKey;

  readonly navLabelFields: StructuredJsonFieldDef[] = [
    { key: 'fa-IR', type: 'string', labelKey: 'catalog.storefront.fields.labelFa', required: true },
    { key: 'en-US', type: 'string', labelKey: 'catalog.storefront.fields.labelEn', required: true }
  ];

  readonly navLocationOptions = STOREFRONT_NAV_LOCATION_KEYS.map(value => ({
    value,
    labelKey: `catalog.selectors.navLocation.${value}`
  }));

  readonly navTargetTypeOptions = STOREFRONT_NAV_TARGET_TYPES.map(value => ({
    value,
    labelKey: value === 'Route'
      ? 'platform.geography.levels.route'
      : value === 'Category'
        ? 'catalog.storefront.categoryNavigation.fields.category'
        : 'catalog.storefront.publishing.offering'
  }));

  selectedStoreId: string | null = null;
  placementDialogStoreId: string | null = null;
  navDialogStoreId: string | null = null;
  placementDialogVisible = false;
  editingPlacement = false;
  navDialogVisible = false;
  createCategoryDialogVisible = false;
  selectedCategoryId: string | null = null;
  newCategoryName = '';
  newCategorySlug = '';
  categoryReloadToken = 0;
  placementOrder = 10;
  placementVisible = true;
  placementFeatured = false;
  placementShowInNav = true;
  navLocationKey = 'header';
  navLabelJson = '{"fa-IR":"عنوان","en-US":"Title"}';
  navTargetType = 'Route';
  navTargetValue: string | null = '/';
  navDisplayOrder = 10;
  navIsVisible = true;

  ngOnInit(): void {
    this.searchPlacements.set(this.route.snapshot.queryParamMap.get('qPlacements') ?? '');
    this.searchNav.set(this.route.snapshot.queryParamMap.get('qNav') ?? '');
    this.service.listCategories().subscribe({ next: rows => this.categories.set(rows) });
    this.service.listOfferings().subscribe({ next: rows => this.offerings.set(rows) });
    if (!this.selectedStoreId) {
      this.loading.set(false);
    } else {
      this.load();
    }
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void unsubscribeMarketplaceOperationsLiveUpdates(this.liveUpdateService, this.marketplaceLive);
  }

  onStoreChange(): void {
    this.selectedPlacements.set([]);
    this.selectedNav.set([]);
    this.load();
  }

  load(): void {
    if (!this.selectedStoreId) {
      this.loading.set(false);
      this.placements.set([]);
      this.navigationItems.set([]);
      return;
    }

    this.loading.set(true);
    this.info.set('');
    this.service.listStorefrontCategoryPlacements(this.selectedStoreId).subscribe({
      next: rows => this.placements.set(rows),
      error: () => this.placements.set([])
    });
    this.service.listStorefrontNavigationItems(this.selectedStoreId).subscribe({
      next: rows => {
        this.navigationItems.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.navigationItems.set([]);
        this.loading.set(false);
      }
    });
  }

  filteredPlacements(): StorefrontCategoryPlacement[] {
    const value = this.searchPlacements().trim().toLowerCase();
    const filtered = !value
      ? this.placements()
      : this.placements().filter(row =>
        this.categoryLabel(row.categoryId).toLowerCase().includes(value)
        || row.categoryId.toLowerCase().includes(value));

    const field = this.placementSortField();
    const order = this.placementSortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field]) * order);
  }

  filteredNavigation(): StorefrontNavigationItem[] {
    const value = this.searchNav().trim().toLowerCase();
    const filtered = !value
      ? this.navigationItems()
      : this.navigationItems().filter(row =>
        this.navLocationLabel(row.locationKey).toLowerCase().includes(value)
        || row.locationKey.toLowerCase().includes(value)
        || this.navLabel(row.labelJson).toLowerCase().includes(value)
        || row.targetType.toLowerCase().includes(value)
        || this.navTargetLabel(row.targetType, row.targetValue).toLowerCase().includes(value));

    const field = this.navSortField();
    const order = this.navSortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field]) * order);
  }

  onPlacementSearch(event: Event): void {
    this.searchPlacements.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { qPlacements: this.searchPlacements().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onNavSearch(event: Event): void {
    this.searchNav.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { qNav: this.searchNav().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onPlacementSelectionChange(selection: StorefrontCategoryPlacement[] | StorefrontCategoryPlacement | null): void {
    this.selectedPlacements.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  onNavSelectionChange(selection: StorefrontNavigationItem[] | StorefrontNavigationItem | null): void {
    this.selectedNav.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  sortPlacementsBy(field: PlacementSortField): void {
    toggleSortField(
      this.placementSortField(),
      field,
      this.placementSortOrder(),
      value => this.placementSortField.set(value),
      value => this.placementSortOrder.set(value)
    );
  }

  sortNavBy(field: NavSortField): void {
    toggleSortField(
      this.navSortField(),
      field,
      this.navSortOrder(),
      value => this.navSortField.set(value),
      value => this.navSortOrder.set(value)
    );
  }

  applyPlacementBulk(): void {
    if (this.selectedPlacements().length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.bulkNothingSelected'));
      return;
    }
    this.info.set(this.t('catalog.marketplace.actions.bulkQueued'));
  }

  applyNavBulk(): void {
    if (this.selectedNav().length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.bulkNothingSelected'));
      return;
    }
    this.info.set(this.t('catalog.marketplace.actions.bulkQueued'));
  }

  exportPlacements(): void {
    const rows = this.filteredPlacements();
    if (rows.length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.exportEmpty'));
      return;
    }
    downloadCsv(
      'storefront-category-placements.csv',
      ['Category', 'Visible', 'Featured', 'ShowInNavigation', 'DisplayOrder'],
      rows.map(row => [
        this.categoryLabel(row.categoryId),
        row.isVisible ? 'Yes' : 'No',
        row.isFeatured ? 'Yes' : 'No',
        row.showInNavigation ? 'Yes' : 'No',
        row.displayOrder.toString()
      ]));
    this.info.set(this.t('catalog.marketplace.actions.exportReady'));
  }

  exportNavigation(): void {
    const rows = this.filteredNavigation();
    if (rows.length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.exportEmpty'));
      return;
    }
    downloadCsv(
      'storefront-navigation-items.csv',
      ['Location', 'Label', 'TargetType', 'TargetValue', 'Visible', 'DisplayOrder'],
      rows.map(row => [
        this.navLocationLabel(row.locationKey),
        this.navLabel(row.labelJson),
        row.targetType,
        this.navTargetLabel(row.targetType, row.targetValue),
        row.isVisible ? 'Yes' : 'No',
        row.displayOrder.toString()
      ]));
    this.info.set(this.t('catalog.marketplace.actions.exportReady'));
  }

  openEditPlacementDialog(row: { storeId: string; categoryId: string; isVisible: boolean; isFeatured: boolean; showInNavigation: boolean; displayOrder: number }): void {
    this.editingPlacement = true;
    this.placementDialogStoreId = row.storeId;
    this.selectedCategoryId = row.categoryId;
    this.placementOrder = row.displayOrder;
    this.placementVisible = row.isVisible;
    this.placementFeatured = row.isFeatured;
    this.placementShowInNav = row.showInNavigation;
    this.placementDialogError.set('');
    this.placementDialogVisible = true;
  }

  openPlacementDialog(): void {
    this.editingPlacement = false;
    this.placementDialogStoreId = this.selectedStoreId;
    this.selectedCategoryId = null;
    this.placementOrder = (this.placements().length + 1) * 10;
    this.placementVisible = true;
    this.placementFeatured = false;
    this.placementShowInNav = true;
    this.placementDialogError.set('');
    this.placementDialogVisible = true;
  }

  closePlacementDialog(): void {
    this.placementDialogVisible = false;
    this.placementDialogError.set('');
  }

  openCreateCategoryDialog(): void {
    this.newCategoryName = '';
    this.newCategorySlug = '';
    this.createCategoryError.set('');
    this.createCategoryDialogVisible = true;
  }

  submitCreateCategory(): void {
    const name = this.newCategoryName.trim();
    const slug = this.newCategorySlug.trim().toLowerCase().replace(/\s+/g, '-');
    if (!name || !slug) {
      this.createCategoryError.set(this.t('catalog.storefront.categoryNavigation.validation.categoryRequired'));
      return;
    }

    this.acting.set(true);
    this.service.createCategory({ name, slug }).subscribe({
      next: category => {
        this.categories.update(rows => [...rows, category]);
        this.selectedCategoryId = category.id;
        this.categoryReloadToken++;
        this.createCategoryDialogVisible = false;
        this.acting.set(false);
      },
      error: () => {
        this.createCategoryError.set(this.t('catalog.storefront.categoryNavigation.validation.saveFailed'));
        this.acting.set(false);
      }
    });
  }

  onNavTargetTypeChange(): void {
    this.navTargetValue = this.navTargetType === 'Route' ? '/' : '';
  }

  openNavDialog(): void {
    this.navDialogStoreId = this.selectedStoreId;
    this.navLocationKey = 'header';
    this.navLabelJson = '{"fa-IR":"عنوان","en-US":"Title"}';
    this.navTargetType = 'Route';
    this.navTargetValue = '/';
    this.navDisplayOrder = (this.navigationItems().length + 1) * 10;
    this.navIsVisible = true;
    this.navDialogError.set('');
    this.navDialogVisible = true;
  }

  closeNavDialog(): void {
    this.navDialogVisible = false;
    this.navDialogError.set('');
  }

  savePlacement(): void {
    this.placementDialogError.set('');
    const storeId = this.placementDialogStoreId?.trim();
    const categoryId = this.selectedCategoryId?.trim();
    if (!storeId) {
      this.placementDialogError.set(this.t('catalog.storefront.categoryNavigation.validation.storeRequired'));
      return;
    }
    if (!categoryId) {
      this.placementDialogError.set(this.t('catalog.storefront.categoryNavigation.validation.categoryRequired'));
      return;
    }

    this.acting.set(true);
    this.service.upsertStorefrontCategoryPlacement({
      storeId,
      categoryId,
      isVisible: this.placementVisible,
      isFeatured: this.placementFeatured,
      showInNavigation: this.placementShowInNav,
      displayOrder: this.placementOrder
    }).subscribe({
      next: () => {
        this.selectedStoreId = storeId;
        this.closePlacementDialog();
        this.load();
        this.acting.set(false);
      },
      error: () => {
        this.placementDialogError.set(this.t('catalog.storefront.categoryNavigation.validation.saveFailed'));
        this.acting.set(false);
      }
    });
  }

  saveNav(): void {
    this.navDialogError.set('');
    const storeId = this.navDialogStoreId?.trim();
    const targetValue = (this.navTargetValue ?? '').trim();
    if (!storeId) {
      this.navDialogError.set(this.t('catalog.storefront.categoryNavigation.validation.storeRequired'));
      return;
    }
    if (!this.navLocationKey.trim() || !targetValue) {
      this.navDialogError.set(this.t('catalog.storefront.categoryNavigation.validation.navTargetRequired'));
      return;
    }

    this.acting.set(true);
    this.service.upsertStorefrontNavigationItem({
      storeId,
      locationKey: this.navLocationKey.trim(),
      parentItemId: null,
      labelJson: this.navLabelJson.trim() || '{}',
      targetType: this.navTargetType.trim(),
      targetValue,
      isVisible: this.navIsVisible,
      displayOrder: this.navDisplayOrder
    }).subscribe({
      next: () => {
        this.selectedStoreId = storeId;
        this.closeNavDialog();
        this.load();
        this.acting.set(false);
      },
      error: () => {
        this.navDialogError.set(this.t('catalog.storefront.categoryNavigation.validation.saveFailed'));
        this.acting.set(false);
      }
    });
  }

  categoryLabel(categoryId: string): string {
    const category = this.categories().find(item => item.id === categoryId);
    return category ? `${category.path} (${category.slug})` : categoryId;
  }

  offeringLabel(offeringId: string): string {
    const offering = this.offerings().find(item => item.id === offeringId);
    return offering ? `${offering.code} - ${offering.title}` : offeringId;
  }

  navLocationLabel(locationKey: string): string {
    return this.t(`catalog.selectors.navLocation.${locationKey}`);
  }

  navTargetLabel(targetType: string, targetValue: string): string {
    if (targetType === 'Category') {
      return this.categoryLabel(targetValue);
    }
    if (targetType === 'Offering') {
      return this.offeringLabel(targetValue);
    }
    return targetValue;
  }

  navLabel(labelJson: string): string {
    try {
      const labels = JSON.parse(labelJson) as Record<string, string>;
      return labels['fa-IR'] || labels['en-US'] || labelJson;
    } catch {
      return labelJson;
    }
  }

  private async startLiveUpdates(): Promise<void> {
    this.marketplaceLive = await subscribeMarketplaceOperationsLiveUpdates(
      this.currentContext.context()?.tenantId,
      this.liveUpdateService,
      () => this.load()
    );
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
